import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

type Q = {
  id: string; reason: string; ttl_expires_at: string;
  created_at: string; released_at: string | null; release_reason: string | null;
};

export default function MemoriaQuarentena() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [items, setItems] = useState<Q[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (oid: string) => {
    const { data, error: e } = await supabase.from("quarantined_memories")
      .select("*").eq("org_id", oid).order("created_at", { ascending: false }).limit(100);
    if (e) throw e;
    setItems((data as Q[]) ?? []);
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: m, error: mErr } = await supabase.from("org_members")
          .select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
        if (mErr) throw mErr;
        if (!m || cancelled) return;
        setOrgId(m.org_id);
        await load(m.org_id);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Falha ao carregar quarentena");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const release = async (id: string) => {
    if (reason.trim().length < 10) {
      toast({ title: "Motivo curto", description: "Mínimo de 10 caracteres.", variant: "destructive" });
      return;
    }
    const { error } = await (supabase.rpc as unknown as (fn: string, args: unknown) => Promise<{ error: { message: string } | null }>)("release_quarantined_memory", { p_id: id, p_reason: reason });
    if (error) {
      toast({ title: "Falhou", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Liberada", description: "Memória liberada da quarentena." });
    setOpen(null); setReason("");
    if (orgId) await load(orgId);
  };

  return (
    <div className="container mx-auto p-6 space-y-4">
      <h1 className="text-3xl font-bold">Quarentena de memórias</h1>
      <p className="text-muted-foreground">TTL de 30 dias · liberação requer motivo (≥10 caracteres)</p>

      <Card>
        <CardHeader><CardTitle>{items.length} item(ns)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma memória em quarentena.</p>}
          {items.map((q) => {
            const ttlDays = Math.max(0, Math.round((new Date(q.ttl_expires_at).getTime() - Date.now()) / 86_400_000));
            const released = !!q.released_at;
            return (
              <div key={q.id} className="border border-border rounded-lg p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={released ? "secondary" : "outline"}>
                      {released ? "Liberada" : `${ttlDays}d restantes`}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(q.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <p className="mt-1 text-sm">{q.reason}</p>
                  {released && q.release_reason && (
                    <p className="mt-1 text-xs text-muted-foreground">Motivo da liberação: {q.release_reason}</p>
                  )}
                </div>
                {!released && (
                  <Dialog open={open === q.id} onOpenChange={(o) => setOpen(o ? q.id : null)}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline">Liberar</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Liberar com motivo</DialogTitle></DialogHeader>
                      <Textarea
                        placeholder="Explique por que essa memória pode voltar (≥10 caracteres)…"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                      />
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(null)}>Cancelar</Button>
                        <Button onClick={() => release(q.id)}>Confirmar liberação</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
